import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Loader2, Eye, Archive, ShieldAlert, RefreshCw, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const HOURS_OPTIONS = [
  { label: "1h", value: 1 },
  { label: "6h", value: 6 },
  { label: "24h", value: 24 },
  { label: "3d", value: 72 },
  { label: "7d", value: 168 },
];

function ScoreBadge({ score }) {
  if (score == null) return <span className="mono text-xs text-zinc-500">—</span>;
  const pct = Math.round(score * 100);
  const color =
    score >= 0.85 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : score >= 0.7 ? "text-amber-400 border-amber-500/30 bg-amber-500/10"
    : "text-red-400 border-red-500/30 bg-red-500/10";
  return (
    <span className={`mono text-xs px-1.5 py-0.5 border rounded-sm ${color}`}>
      {pct}
    </span>
  );
}

function SignalRow({ label, value, ok }) {
  return (
    <div className="flex items-center gap-2 text-[11px] mono">
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-zinc-700"}`} />
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-300 ml-auto">{value}</span>
    </div>
  );
}

export default function AutoReview() {
  const [items, setItems] = useState([]);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [siteFilter, setSiteFilter] = useState("");
  const [sites, setSites] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/sites")
      .then(r => { if (!cancelled) setSites(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ hours: String(hours), limit: "100" });
      if (siteFilter) params.set("site_id", siteFilter);
      const r = await api.get(`/articles/auto/recent?${params.toString()}`);
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [hours, siteFilter]);  // eslint-disable-line

  async function unpublish(id) {
    if (!window.confirm("Archive this auto-published article? It will be removed from the public site.")) return;
    setBusyId(id);
    try {
      await api.post(`/articles/${id}/archive`);
      setItems(items.filter(a => a.id !== id));
    } finally { setBusyId(null); }
  }

  async function rescore(id) {
    setBusyId(id);
    try {
      const r = await api.post(`/articles/${id}/rescore`);
      setItems(items.map(a => a.id === id
        ? { ...a, quality_score: r.data.score, quality_signals: { ...r.data.signals, blockers: r.data.blockers } }
        : a));
    } finally { setBusyId(null); }
  }

  const siteMap = useMemo(() => {
    const m = {};
    sites.forEach(s => { m[s.id] = s; });
    return m;
  }, [sites]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="autoreview-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Auto-Publish Review</h1>
          <div className="text-zinc-500 text-sm mt-1 mono">
            Articles that quality gates approved and shipped without your click. Spot-check, unpublish any that slipped.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="autoreview-site-filter"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
          >
            <option value="">All sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="flex border border-zinc-800 rounded-sm overflow-hidden">
            {HOURS_OPTIONS.map(h => (
              <button
                key={h.value}
                data-testid={`autoreview-hours-${h.value}`}
                onClick={() => setHours(h.value)}
                className={`px-3 py-2 text-xs mono uppercase tracking-wider transition-colors ${
                  hours === h.value
                    ? "bg-white text-zinc-950"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`}
              >
                {h.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="autoreview-refresh"
            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:bg-zinc-900 px-3 py-2 rounded-sm text-xs text-zinc-300 disabled:opacity-50"
          >
            {loading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Refresh
          </button>
        </div>
      </div>

      {!loading && items.length === 0 && (
        <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-10 text-center">
          <div className="font-display text-lg text-zinc-300">No auto-published articles in the last {hours}h.</div>
          <div className="text-xs text-zinc-500 mono mt-2">
            Turn on auto-publish under <span className="text-zinc-300">Site → Settings → Auto-Publish</span>, then come back.
          </div>
        </div>
      )}

      <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800">
        {items.map(a => {
          const site = siteMap[a.site_id];
          const signals = a.quality_signals || {};
          const blockers = signals.blockers || [];
          const isOpen = openId === a.id;
          return (
            <div key={a.id} data-testid={`autoreview-item-${a.id}`}>
              <div className="px-4 py-3 flex items-center gap-3 hover:bg-zinc-900/40">
                <ScoreBadge score={a.quality_score} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">{a.title || a.original_title}</div>
                  <div className="text-[11px] text-zinc-500 mono mt-0.5 flex items-center gap-2">
                    <span>{site?.name || a.site_id}</span>
                    <span className="text-zinc-700">·</span>
                    <span>{a.category_slug || "uncategorized"}</span>
                    <span className="text-zinc-700">·</span>
                    <span>{(a.published_at || "").slice(0, 16).replace("T", " ")}</span>
                    {blockers.length > 0 && (
                      <>
                        <span className="text-zinc-700">·</span>
                        <span className="inline-flex items-center gap-1 text-red-400">
                          <ShieldAlert size={11} /> {blockers.join(", ")}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setOpenId(isOpen ? null : a.id)}
                  className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white px-2 py-1"
                  data-testid={`autoreview-inspect-${a.id}`}
                >
                  <Eye size={12} /> {isOpen ? "hide" : "inspect"}
                </button>
                {a.slug && site && (
                  <Link
                    to={`/site/${site.slug}/article/${a.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white px-2 py-1"
                  >
                    <ExternalLink size={12} /> view
                  </Link>
                )}
                <button
                  onClick={() => rescore(a.id)}
                  disabled={busyId === a.id}
                  className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white disabled:opacity-50 px-2 py-1"
                  data-testid={`autoreview-rescore-${a.id}`}
                >
                  <RefreshCw size={12} /> rescore
                </button>
                <button
                  onClick={() => unpublish(a.id)}
                  disabled={busyId === a.id}
                  className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 disabled:opacity-50 px-2 py-1"
                  data-testid={`autoreview-unpublish-${a.id}`}
                >
                  <Archive size={12} /> unpublish
                </button>
              </div>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 bg-zinc-950/60">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-900/40">
                      <div className="label-tiny mb-2">Quality Signals</div>
                      <div className="space-y-1">
                        <SignalRow label="Word count" value={signals.word_count ?? "—"} ok={(signals.word_count_score ?? 0) >= 0.5} />
                        <SignalRow label="Originality" value={signals.originality?.toFixed?.(2) ?? "—"} ok={(signals.originality ?? 0) >= 0.6} />
                        <SignalRow label="Niche fit" value={signals.niche_fit?.toFixed?.(2) ?? "n/a"} ok={(signals.niche_fit ?? 0.6) >= 0.5} />
                        <SignalRow label="Outlink reachable" value={String(signals.outlink_ok ?? "?")} ok={signals.outlink_ok === true} />
                        <SignalRow label="Image present" value={String(signals.image_ok ?? false)} ok={!!signals.image_ok} />
                        <SignalRow label="Ads-safe" value={String(signals.ads_safe ?? true)} ok={!!signals.ads_safe} />
                        <SignalRow label="SEO complete" value={signals.has_seo?.toFixed?.(2) ?? "—"} ok={(signals.has_seo ?? 0) >= 0.75} />
                      </div>
                    </div>
                    <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-900/40">
                      <div className="label-tiny mb-2">Article Preview</div>
                      <div className="text-sm text-zinc-200 font-medium">{a.title}</div>
                      <div className="text-xs text-zinc-400 mt-2 leading-relaxed line-clamp-6">
                        {a.summary || a.body_markdown?.slice(0, 280) || "(no summary)"}
                      </div>
                      <div className="text-[11px] text-zinc-500 mono mt-2">
                        Source: {a.source_name}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
